// Backlog Management Context Provider

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@auth';
import type { Backlog, BacklogItem } from './types';
import * as backlogApi from './api';

interface BacklogContextValue {
  backlogs: Backlog[];
  currentBacklog: Backlog | null;
  loading: boolean;
  error: string | null;
  fetchBacklogs: () => Promise<void>;
  fetchBacklog: (id: number) => Promise<void>;
  updateItem: (backlogId: number, item: BacklogItem) => Promise<void>;
  deleteItem: (backlogId: number, itemId: string) => Promise<void>;
  reorderItems: (backlogId: number, items: BacklogItem[]) => Promise<void>;
  setCurrentBacklog: (backlog: Backlog | null) => void;
}

const BacklogContext = createContext<BacklogContextValue | undefined>(undefined);

export const useBacklog = () => {
  const context = useContext(BacklogContext);
  if (!context) {
    throw new Error('useBacklog must be used within BacklogProvider');
  }
  return context;
};

interface BacklogProviderProps {
  children: ReactNode;
}

export const BacklogProvider: React.FC<BacklogProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [backlogs, setBacklogs] = useState<Backlog[]>([]);
  const [currentBacklog, setCurrentBacklog] = useState<Backlog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBacklogs = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);
    try {
      const data = await backlogApi.getBacklogs();
      setBacklogs(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch backlogs';
      setError(message);
      console.error('Error fetching backlogs:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchBacklog = useCallback(async (id: number) => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);
    try {
      const data = await backlogApi.getBacklog(id);
      setCurrentBacklog(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch backlog';
      setError(message);
      console.error('Error fetching backlog:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const updateItem = useCallback(async (backlogId: number, item: BacklogItem) => {
    if (!isAuthenticated) return;

    try {
      await backlogApi.updateBacklogItem(backlogId, item);
      
      // Update current backlog if it matches
      if (currentBacklog && currentBacklog.id === backlogId) {
        setCurrentBacklog({
          ...currentBacklog,
          items: currentBacklog.items?.map((i) =>
            i.id === item.id ? item : i
          ),
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update item';
      setError(message);
      console.error('Error updating item:', err);
      throw err;
    }
  }, [isAuthenticated, currentBacklog]);

  const deleteItem = useCallback(async (backlogId: number, itemId: string) => {
    if (!isAuthenticated) return;

    try {
      await backlogApi.deleteBacklogItem(backlogId, itemId);
      
      // Update current backlog if it matches
      if (currentBacklog && currentBacklog.id === backlogId) {
        setCurrentBacklog({
          ...currentBacklog,
          items: currentBacklog.items?.filter((i) => i.id !== itemId),
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete item';
      setError(message);
      console.error('Error deleting item:', err);
      throw err;
    }
  }, [isAuthenticated, currentBacklog]);

  const reorderItems = useCallback(async (backlogId: number, items: BacklogItem[]) => {
    if (!isAuthenticated) return;

    try {
      const positions = items.map((item, index) => ({
        id: item.id,
        position: index,
      }));
      
      await backlogApi.reorderBacklogItems(backlogId, positions);
      
      // Update current backlog if it matches
      if (currentBacklog && currentBacklog.id === backlogId) {
        setCurrentBacklog({
          ...currentBacklog,
          items,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reorder items';
      setError(message);
      console.error('Error reordering items:', err);
      throw err;
    }
  }, [isAuthenticated, currentBacklog]);

  const value: BacklogContextValue = {
    backlogs,
    currentBacklog,
    loading,
    error,
    fetchBacklogs,
    fetchBacklog,
    updateItem,
    deleteItem,
    reorderItems,
    setCurrentBacklog,
  };

  return (
    <BacklogContext.Provider value={value}>
      {children}
    </BacklogContext.Provider>
  );
};
